import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
  Cell,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import { ListFilter, LoaderCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  smallTextSize,
  secondaryText,
  semiBoldText,
  whiteBG,
  borderPrimary,
} from '../../../styling/stylingHelper';
import { MenuEngineeringDataPoint } from '../../../utils/chartsUtils';
import { MultiSelectFilter } from '../../ui/MultiSelectFilter';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { useMenusStore } from '../../../stores/appMenusStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useChartTheme } from '../../../utils/chartTheme';

const UNCATEGORIZED_ID = '__uncategorized__';

interface MenuEngineeringChartProps {
  data: MenuEngineeringDataPoint[];
  hasEnoughData?: boolean;
  timeWindow?: '2months' | '1month' | '1week' | null;
  loading?: boolean;
}

export const MenuEngineeringChart: React.FC<MenuEngineeringChartProps> = ({ data, hasEnoughData = true, timeWindow, loading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useChartTheme();
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const menuFilterRef = useRef<HTMLDivElement>(null);

  // Category filter state (persisted in store)
  const chartCategoryFilters = useFiltersStore(s => s.chartCategoryFilters);
  const setChartCategoryFilters = useFiltersStore(s => s.setChartCategoryFilters);
  const categories = useCostPriceCategoriesStore(s => s.categories);

  // Menu filter state (persisted in store)
  const chartMenuFilters = useFiltersStore(s => s.chartMenuFilters);
  const setChartMenuFilters = useFiltersStore(s => s.setChartMenuFilters);
  const chartMenuFiltersInitForRestaurantId = useFiltersStore(s => s.chartMenuFiltersInitForRestaurantId);
  const setChartMenuFiltersInitForRestaurantId = useFiltersStore(s => s.setChartMenuFiltersInitForRestaurantId);
  const menus = useMenusStore(s => s.menus);
  const menuItemsMap = useMenusStore(s => s.menuItems);
  const loadMenuItems = useMenusStore(s => s.loadMenuItems);

  // Restaurant context (used to (re)initialize the menu filter on restaurant change)
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = !!selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (menuFilterRef.current && !menuFilterRef.current.contains(event.target as Node)) {
        setShowMenuDropdown(false);
      }
    };
    if (showCategoryDropdown || showMenuDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown, showMenuDropdown]);

  // Pre-load items for every menu so toggling the filter is instant.
  useEffect(() => {
    menus.forEach(m => {
      if (!menuItemsMap[m.id]) {
        loadMenuItems(m.id);
      }
    });
  }, [menus, menuItemsMap, loadMenuItems]);

  // On first load (per restaurant), pre-select all active menus.
  // If there are no active menus, leave empty (= show all).
  // Init gate lives in the filters store so navigating away & back preserves the
  // user's manual selection (a local ref would reset on remount).
  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    if (chartMenuFiltersInitForRestaurantId === restaurantId) return;
    if (menus.length === 0) return;
    const activeMenuIds = menus.filter(m => m.isActive).map(m => m.id);
    if (activeMenuIds.length > 0) {
      setChartMenuFilters(activeMenuIds);
    } else {
      setChartMenuFilters([]);
    }
    setChartMenuFiltersInitForRestaurantId(restaurantId);
  }, [restaurantId, isAllRestaurants, menus, chartMenuFiltersInitForRestaurantId, setChartMenuFilters, setChartMenuFiltersInitForRestaurantId]);

  // Build available category options from the data
  const availableCategoryOptions = useMemo(() => {
    const categoryIds = new Set(data.map(d => d.categoryId || UNCATEGORIZED_ID));
    const options: { id: string; name: string }[] = [];

    categories
      .filter(c => categoryIds.has(c.id))
      .sort((a, b) => a.position - b.position)
      .forEach(c => options.push({ id: c.id, name: c.name }));

    if (categoryIds.has(UNCATEGORIZED_ID)) {
      options.push({ id: UNCATEGORIZED_ID, name: t('charts.uncategorized') });
    }

    return options;
  }, [data, categories, t]);

  // Available menu options (only menus that contain at least one item present in `data`).
  // Active menus surfaced first so they're easier to scan in the dropdown.
  const availableMenuOptions = useMemo(() => {
    const dataIds = new Set(data.map(d => d.id));
    return menus
      .filter(m => (menuItemsMap[m.id] || []).some(id => dataIds.has(id)))
      .slice()
      .sort((a, b) => Number(b.isActive) - Number(a.isActive))
      .map(m => ({ id: m.id, name: m.name }));
  }, [menus, menuItemsMap, data]);

  // Union of cost-price IDs across all selected menus.
  // null = no menu filter applied (show all).
  const selectedMenuItemIds = useMemo(() => {
    if (chartMenuFilters.length === 0) return null;
    const ids = new Set<string>();
    chartMenuFilters.forEach(menuId => {
      (menuItemsMap[menuId] || []).forEach(id => ids.add(id));
    });
    return ids;
  }, [chartMenuFilters, menuItemsMap]);

  // Filter data by selected menus AND selected categories (empty = show all for each)
  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (selectedMenuItemIds && !selectedMenuItemIds.has(d.id)) {
        return false;
      }
      if (chartCategoryFilters.length > 0) {
        const catId = d.categoryId || UNCATEGORIZED_ID;
        if (!chartCategoryFilters.includes(catId)) return false;
      }
      return true;
    });
  }, [data, chartCategoryFilters, selectedMenuItemIds]);

  const formatCurrency = (value: number) => {
    return `€${value.toFixed(2)}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Determine dot color based on menu engineering matrix classification
  // Colors must match the cost price matrix (reference: DishTable.tsx getMatrixBgColor)
  const getDotColor = (matrix: string | null) => {
    switch (matrix) {
      case 'Star':
        return '#16B364'; // Green - Stars (high margin, high volume)
      case 'Puzzle':
        return '#2E90FA'; // Blue - Puzzles (high margin, low volume)
      case 'Plowhorse':
        return '#F1A62D'; // Orange - Plowhorses (low margin, high volume)
      case 'Dog':
        return '#F04438'; // Red - Dogs (low margin, low volume)
      default:
        return '#9CA3AF'; // Gray - Unclassified
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const color = getDotColor(data.matrix);
      return (
        <div className="bg-white dark:bg-background-dark px-3 py-2.5 rounded-lg border border-[#F2F4F7] dark:border-border-dark shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)] dark:shadow-[0px_12px_16px_-4px_rgba(0,0,0,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.2)] flex flex-col gap-1 min-w-[200px] max-w-xs relative">
          <p className="text-[#101828] dark:text-textPrimary-dark font-bold text-[14px] leading-5 truncate mb-2">{data.name}</p>
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.sales')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">{data.timesSold}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.menuPrice')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">{formatCurrency(data.menuPrice)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.costPrice')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">{formatCurrency(data.costPrice)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.costPercentage')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">
                {formatPercentage(data.costPercentage)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.marginPercentage')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">
                {formatPercentage(data.marginPercentage)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.margin')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">{formatCurrency(data.margin)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.marginTotal')}:</span>
              <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]">{formatCurrency(data.margin * data.timesSold)}</span>
            </div>
            {data.matrix && (
              <div className="flex justify-between gap-4 items-center pt-1 border-t border-[#F2F4F7] dark:border-border-dark">
                <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[18px]">{t('charts.classification')}:</span>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }}></span>
                  <span className="text-[#101828] dark:text-textPrimary-dark text-[12px] font-medium leading-[18px]" style={{ color: color }}>
                    {t(`charts.matrix.${data.matrix.toLowerCase()}`)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2.5 h-2.5 bg-white dark:bg-background-dark border-r border-b border-[#F2F4F7] dark:border-border-dark rotate-45"></div>
        </div>
      );
    }
    return null;
  };

  const handleDotClick = (data: MenuEngineeringDataPoint) => {
    // Open the new cost price page with the detail modal pre-opened (deep-link
    // via the `detail` query param) instead of the legacy standalone detail page.
    navigate(`/costprice?detail=${data.id}`);
  };

  if (!hasEnoughData) {
    return (
      <div className={cn(cardClass, 'p-8 text-center')}>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <>
            <p className={cn(secondaryText)}>{t('charts.noMenuEngineeringData')}</p>
            <p className={cn(secondaryText, smallTextSize, 'mt-2')}>
              {t('charts.menuEngineeringRequirements')}
            </p>
          </>
        )}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn(cardClass, 'p-8 text-center')}>
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className={cn(secondaryText)}>{t('charts.noMenuEngineeringData')}</p>
        )}
      </div>
    );
  }

  // Calculate thresholds for quadrants
  const MARGIN_THRESHOLD = 72; // 72% margin threshold
  const hasFilteredData = filteredData.length > 0;
  const validItems = filteredData.filter(item => item.timesSold > 0);
  const averageUnitsSold = validItems.length >= 2
    ? validItems.reduce((sum, item) => sum + item.timesSold, 0) / validItems.length
    : 0;

  // Calculate min/max values for axes with padding (only when we have data)
  const maxSales = hasFilteredData ? Math.max(...filteredData.map(d => d.timesSold)) : 0;
  const minSales = hasFilteredData ? Math.min(...filteredData.map(d => d.timesSold)) : 0;
  const maxMargin = hasFilteredData ? Math.max(...filteredData.map(d => d.marginPercentage)) : 0;
  const minMargin = hasFilteredData ? Math.min(...filteredData.map(d => d.marginPercentage)) : 0;

  // Calculate Y-axis domain with padding (10% of range, minimum 5% of max value)
  const marginRange = maxMargin - minMargin;
  const marginPadding = marginRange > 0
    ? Math.max(marginRange * 0.1, maxMargin * 0.05)
    : maxMargin * 0.1;
  const yAxisMin = Math.max(0, minMargin - marginPadding);
  const yAxisMax = Math.min(100, maxMargin + marginPadding);

  // Calculate X-axis domain with padding
  const salesRange = maxSales - minSales;
  const salesPadding = salesRange > 0
    ? Math.max(salesRange * 0.1, maxSales * 0.05)
    : maxSales * 0.1;
  const xAxisMin = Math.max(0, minSales - salesPadding);
  const xAxisMax = maxSales + salesPadding;

  // Quadrant background colors matching the reference design (dark counterparts
  // are separate, more-saturated-safe tones — not a straight alpha reuse — since
  // the light tints are near-white and would vanish/clash on a dark card).
  const QUADRANT_BG = {
    star: { light: '#EDFCF260', dark: '#064E3B40' },
    puzzle: { light: '#EFF8FF60', dark: '#1D3A8A40' },
    plowhorse: { light: '#FFF4E260', dark: '#78350F40' },
    dog: { light: '#FEF3F260', dark: '#7F1D1D40' },
  };
  const starBgColor = theme.darkMode ? QUADRANT_BG.star.dark : QUADRANT_BG.star.light;
  const puzzleBgColor = theme.darkMode ? QUADRANT_BG.puzzle.dark : QUADRANT_BG.puzzle.light;
  const plowhorseBgColor = theme.darkMode ? QUADRANT_BG.plowhorse.dark : QUADRANT_BG.plowhorse.light;
  const dogBgColor = theme.darkMode ? QUADRANT_BG.dog.dark : QUADRANT_BG.dog.light;

  return (
    <div className="bg-white dark:bg-card-dark p-4 rounded-xl shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
      {/* Header */}
      <div className={cn(hasFilteredData ? "mb-20" : "mb-4", "flex items-center justify-between w-full max-lg:flex-wrap max-lg:gap-y-2")}>
        <div className="max-lg:min-w-0">
          <h1 className="text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1">
            {t('charts.menuEngineeringMatrix')}
          </h1>
          <p className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal">
            {t('charts.menuEngineeringDescription')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {timeWindow && timeWindow !== '2months' && (
            <div className="px-3 py-1.5 bg-[#FFF4E2] dark:bg-amber-500/10 text-[#DB9729] dark:text-darkOrangeColor-dark rounded-lg text-[12px] leading-[18px] font-medium">
              {t(`charts.timeWindow.${timeWindow}`)}
            </div>
          )}
          {availableMenuOptions.length > 0 && (
            <div className="relative" ref={menuFilterRef}>
              <button
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors',
                  smallTextSize,
                  semiBoldText,
                  whiteBG,
                  borderPrimary,
                  secondaryText,
                  'hover:bg-gray-50 dark:hover:bg-white/5'
                )}
              >
                <ListFilter className="w-4 h-4" />
                <span>{t('charts.menuFilter')}</span>
                {chartMenuFilters.length > 0 && (
                  <span className="bg-darkGreenColor dark:bg-darkGreenColor text-white dark:text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {chartMenuFilters.length}
                  </span>
                )}
              </button>
              {showMenuDropdown && (
                <div
                  className={cn(
                    "absolute top-full mt-1 right-0 z-50",
                    "w-[330px] max-w-[calc(100vw-2rem)]",
                    "bg-white dark:bg-card-dark shadow-xl rounded-xl border",
                    borderPrimary,
                    "p-3"
                  )}
                >
                  <MultiSelectFilter
                    label={t('charts.menuFilter')}
                    selectedValues={chartMenuFilters}
                    onValuesChange={setChartMenuFilters}
                    availableOptions={availableMenuOptions}
                    maxHeight="max-h-[250px]"
                  />
                </div>
              )}
            </div>
          )}
          {availableCategoryOptions.length > 1 && (
            <div className="relative" ref={categoryFilterRef}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors',
                  smallTextSize,
                  semiBoldText,
                  whiteBG,
                  borderPrimary,
                  secondaryText,
                  'hover:bg-gray-50 dark:hover:bg-white/5'
                )}
              >
                <ListFilter className="w-4 h-4" />
                <span>{t('charts.categoryFilter')}</span>
                {chartCategoryFilters.length > 0 && (
                  <span className="bg-darkGreenColor dark:bg-darkGreenColor text-white dark:text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {chartCategoryFilters.length}
                  </span>
                )}
              </button>
              {showCategoryDropdown && (
                <div
                  className={cn(
                    "absolute top-full mt-1 right-0 z-50",
                    "w-[330px] max-w-[calc(100vw-2rem)]",
                    "bg-white dark:bg-card-dark shadow-xl rounded-xl border",
                    borderPrimary,
                    "p-3"
                  )}
                >
                  <MultiSelectFilter
                    label={t('charts.categoryFilter')}
                    selectedValues={chartCategoryFilters}
                    onValuesChange={setChartCategoryFilters}
                    availableOptions={availableCategoryOptions}
                    maxHeight="max-h-[250px]"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!hasFilteredData ? (
        <div className="py-12 text-center">
          <p className={cn(secondaryText)}>{t('charts.noMenuEngineeringData')}</p>
        </div>
      ) : (
      <>
      {/* Chart Area */}
      <div className="mt-10 w-full h-[300px] sm:h-[396px] md:px-6 relative">
        <div className="relative w-full h-full sm:border-l sm:border-b border-[#667085] dark:border-textQuaternary-dark">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              {/* Custom grid lines - quadrant dividers */}
              {averageUnitsSold > 0 && MARGIN_THRESHOLD >= yAxisMin && MARGIN_THRESHOLD <= yAxisMax && (
                <>
                  {/* Vertical divider */}
                  <ReferenceLine
                    x={averageUnitsSold}
                    stroke={theme.referenceLine}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                  />
                  {/* Horizontal divider */}
                  <ReferenceLine
                    y={MARGIN_THRESHOLD}
                    stroke={theme.referenceLine}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                  />
                </>
              )}
              
              {/* Quadrant backgrounds */}
              {averageUnitsSold > 0 && MARGIN_THRESHOLD >= yAxisMin && MARGIN_THRESHOLD <= yAxisMax && (
                <>
                  {/* Star quadrant (top right) - High margin, High volume */}
                  <ReferenceArea
                    x1={Math.max(xAxisMin, averageUnitsSold)}
                    x2={xAxisMax}
                    y1={Math.max(yAxisMin, MARGIN_THRESHOLD)}
                    y2={yAxisMax}
                    fill={starBgColor}
                  />
                  {/* Puzzle quadrant (top left) - High margin, Low volume */}
                  <ReferenceArea
                    x1={xAxisMin}
                    x2={Math.min(xAxisMax, averageUnitsSold)}
                    y1={Math.max(yAxisMin, MARGIN_THRESHOLD)}
                    y2={yAxisMax}
                    fill={puzzleBgColor}
                  />
                  {/* Plowhorse quadrant (bottom right) - Low margin, High volume */}
                  <ReferenceArea
                    x1={Math.max(xAxisMin, averageUnitsSold)}
                    x2={xAxisMax}
                    y1={yAxisMin}
                    y2={Math.min(yAxisMax, MARGIN_THRESHOLD)}
                    fill={plowhorseBgColor}
                  />
                  {/* Dog quadrant (bottom left) - Low margin, Low volume */}
                  <ReferenceArea
                    x1={xAxisMin}
                    x2={Math.min(xAxisMax, averageUnitsSold)}
                    y1={yAxisMin}
                    y2={Math.min(yAxisMax, MARGIN_THRESHOLD)}
                    fill={dogBgColor}
                  />
                </>
              )}

              <XAxis
                type="number"
                dataKey="timesSold"
                name={t('charts.sales')}
                tick={{ fontSize: 12, fill: theme.darkMode ? '#9CA3AF' : '#475467', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                tickLine={false}
                axisLine={false}
                domain={[xAxisMin, xAxisMax]}
              />
              <YAxis
                type="number"
                dataKey="marginPercentage"
                name={t('charts.marginPercentage')}
                tickFormatter={formatPercentage}
                tick={{ fontSize: 12, fill: theme.darkMode ? '#9CA3AF' : '#6B7280', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                tickLine={false}
                axisLine={false}
                label={{
                  value: t('charts.marginPercentageYAxis'),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 14, fill: theme.darkMode ? '#D1D5DB' : '#374151', fontFamily: "'Plus Jakarta Sans', sans-serif" }
                }}
                domain={[yAxisMin, yAxisMax]}
              />
              <ZAxis range={[100, 400]} />
              
              <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              <Scatter
                data={filteredData}
                fill="#7047EB"
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getDotColor(entry.matrix)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => handleDotClick(entry)}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>

          {/* Quadrant Labels */}
          {averageUnitsSold > 0 && MARGIN_THRESHOLD >= yAxisMin && MARGIN_THRESHOLD <= yAxisMax && (
            <>
              <div className="absolute -top-12 left-1 text-[#1570EF] dark:text-textBlue-dark bg-[#EFF8FF] dark:bg-blue-500/10 w-24 sm:w-[120px] h-8 sm:h-10 rounded-md sm:rounded-[10px] text-[14px] leading-5 font-medium z-10 flex items-center justify-center pointer-events-none">
                {t('charts.matrix.puzzle')}
              </div>
              <div className="absolute -top-12 right-1 text-[#099250] dark:text-textGreen-dark bg-[#EDFCF5] dark:bg-green-500/10 w-24 sm:w-[120px] h-8 sm:h-10 rounded-md sm:rounded-[10px] text-[14px] leading-5 font-medium z-10 flex items-center justify-center pointer-events-none">
                {t('charts.matrix.star')}
              </div>
              <div className="absolute -bottom-[70px] sm:-bottom-14 left-1 text-[#D92D20] dark:text-textRed-dark bg-[#FEE4E2] dark:bg-red-500/10 w-24 sm:w-[120px] h-8 sm:h-10 rounded-md sm:rounded-[10px] text-[14px] leading-5 font-medium z-10 flex items-center justify-center pointer-events-none">
                {t('charts.matrix.dog')}
              </div>
              <div className="absolute -bottom-[70px] sm:-bottom-14 right-1 text-[#DB9729] dark:text-darkOrangeColor-dark bg-[#FFF4E2] dark:bg-amber-500/10 w-24 sm:w-[120px] h-8 sm:h-10 rounded-md sm:rounded-[10px] text-[14px] leading-5 font-medium z-10 flex items-center justify-center pointer-events-none">
                {t('charts.matrix.plowhorse')}
              </div>
            </>
          )}

          {/* X-axis label */}
          <div className="absolute -bottom-7 w-full text-center text-[#374151] dark:text-textSecondary-dark text-[14px] leading-4 font-normal">
            {t('charts.salesXAxis')}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-20 md:mt-12 flex items-center flex-wrap justify-center gap-3 sm:gap-6">
        <div className="flex items-center gap-2 text-nowrap">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDotColor('Star') }}></div>
          <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">
            {t('charts.matrix.star')}
          </span>
        </div>
        <div className="flex items-center gap-2 text-nowrap">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDotColor('Puzzle') }}></div>
          <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">
            {t('charts.matrix.puzzle')}
          </span>
        </div>
        <div className="flex items-center gap-2 text-nowrap">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDotColor('Plowhorse') }}></div>
          <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">
            {t('charts.matrix.plowhorse')}
          </span>
        </div>
        <div className="flex items-center gap-2 text-nowrap">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDotColor('Dog') }}></div>
          <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">
            {t('charts.matrix.dog')}
          </span>
        </div>
        {filteredData.some(item => !item.matrix) && (
          <div className="flex items-center gap-2 text-nowrap">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDotColor(null) }}></div>
            <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">
              {t('charts.matrix.unclassified')}
            </span>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};
